import { shippingLabelBuilderFlag } from "@redotech/redo-fulfillment-common/oms-feature-flags";
import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  FulfillmentFlatTeamSettingGroupById,
  type FulfillmentFlatTeamSettingGroupId,
} from "./groups";

export type FulfillmentFlatTeamSettingKey =
  | "outboundLabels/suggestMerge/mergeConditions"
  | "outboundLabels/autoMerge/mergeConditions"
  | "outboundLabels/learnedPresets/parcel"
  | "outboundLabels/learnedPresets/weight"
  | "outboundLabels/learnedPresets/keyLevel"
  | "outboundLabels/batchSuggestions/statusOption"
  | "outboundLabels/batchSuggestions/minGroupCount"
  | "outboundLabels/batchSuggestions/groupingKey"
  | "outboundLabels/defaultShipFromLocationExternalId"
  | "outboundLabels/address/internationalAddressOutreach"
  | "outboundLabels/documents/packingSlip/perParcelSlips"
  | "outboundLabels/documents/pickList/emphasizeQuantity"
  | "outboundLabels/documents/pickList/perOrder"
  | "outboundLabels/documents/shippingLabel/customSectionEnabled"
  | "outboundLabels/disableKeyboardOnScan"
  | "outboundLabels/scanVerify/emphasizeNotesScreen"
  | "outboundLabels/scanVerify/manualQuantityEntry"
  | "outboundLabels/scanVerify/moveScannedItemToTop"
  | "outboundLabels/scanVerify/moveVerifiedItemsToBottom"
  | "outboundLabels/scanVerify/autoVerifyLabeledOrders"
  | "outboundLabels/scanVerify/allowManualQuantityAfterBarcodeScan"
  | "outboundLabels/scanVerify/labelAutoPrintTrigger"
  | "outboundLabels/internalNotifications/notifyOnCarrierConnectionInterrupted"
  | "outboundLabels/wms/transferPutawayOrder"
  | "outboundLabels/wms/excludeEmptyTransferDestinations"
  | "outboundLabels/wms/emptyInventoryLevelCleanup"
  | "outboundLabels/wms/receivingMode"
  | "outboundLabels/wms/pickSession/forceSingleItemPickSessions"
  | "outboundLabels/wms/pickSession/scanOncePerProduct"
  | "outboundLabels/wms/defaultReplenishmentQuantity"
  | "outboundLabels/wms/dashboard"
  | "outboundLabels/wms/pickSession/scanType"
  | "outboundLabels/wms/pickSession/defaultOrderCount"
  | "outboundLabels/wms/pickSession/maxItemCount"
  | "outboundLabels/wms/bulkPickSession"
  | "outboundLabels/wms/pickSession/stalePickedRelease"
  | "outboundLabels/wms/pickSession/sameBinSortMode"
  | "outboundLabels/wms/showOrderNotesInPickSession"
  | "outboundLabels/wms/preferFullPickTitle"
  | "outboundLabels/wms/pickSession/allowPaymentPendingPicks"
  | "outboundLabels/wms/pickSession/zonePickingMode"
  | "outboundLabels/rejectUnknownItemsFromPicks"
  | "outboundLabels/wms/hideManualToteAssignment"
  | "outboundLabels/wms/preferRedoBinsOverMarketplace"
  | "outboundLabels/wms/lotTracking"
  | "outboundLabels/wms/barcodeLabelSize"
  | "outboundLabels/wms/binNaming"
  | "outboundLabels/wms/defaultFulfillmentLocationExternalId"
  | "outboundLabels/autoSplit/splitConditions"
  | (typeof orderManagementSettings)[number]["id"];

const shipFromLocationKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/defaultShipFromLocationExternalId";
const shipFromLocationGroupId = "fulfillment.ship_from_locations" as const;

const internationalAddressOutreachKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/address/internationalAddressOutreach";
const addressGroupId = "fulfillment.address" as const;

const perParcelSlipsKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/documents/packingSlip/perParcelSlips";
const emphasizeQuantityKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/documents/pickList/emphasizeQuantity";
const pickListPerOrderKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/documents/pickList/perOrder";
const shippingLabelCustomSectionKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/documents/shippingLabel/customSectionEnabled";
const printingGroupId = "fulfillment.printing" as const;

const emphasizeNotesScreenKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/emphasizeNotesScreen";
const manualQuantityEntryKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/manualQuantityEntry";
const moveScannedItemToTopKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/moveScannedItemToTop";
const moveVerifiedItemsToBottomKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/moveVerifiedItemsToBottom";
const autoVerifyLabeledOrdersKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/autoVerifyLabeledOrders";
const allowManualQuantityAfterBarcodeScanKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/allowManualQuantityAfterBarcodeScan";
const labelAutoPrintTriggerKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/labelAutoPrintTrigger";
const disableKeyboardOnScanKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/disableKeyboardOnScan";
const scanToVerifyGroupId = "fulfillment.scan_to_verify" as const;

const carrierConnectionInterruptedKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/internalNotifications/notifyOnCarrierConnectionInterrupted";
const outboundNotificationsGroupId =
  "fulfillment.outbound_notifications" as const;

const transferPutawayOrderKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/transferPutawayOrder";
const excludeEmptyTransferDestinationsKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/excludeEmptyTransferDestinations";
const emptyInventoryLevelCleanupKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/emptyInventoryLevelCleanup";
const receivingModeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/receivingMode";
const transfersGroupId = "fulfillment.transfers" as const;

const forceSingleItemPickSessionsKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/forceSingleItemPickSessions";
const scanOncePerProductKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/scanOncePerProduct";
const pickSessionsGroupId = "fulfillment.pick_sessions" as const;

const defaultReplenishmentQuantityKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/defaultReplenishmentQuantity";
const replenishmentGroupId = "fulfillment.replenishment" as const;

const dashboardActionsKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/dashboard";
const dashboardGroupId = "fulfillment.dashboard" as const;
const workOrdersGroupId = "fulfillment.work_orders" as const;
const generalGroupId = "fulfillment.general" as const;
const scanTypeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/scanType";
const defaultOrderCountKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/defaultOrderCount";
const maxItemCountKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/maxItemCount";
const bulkPickSessionKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/bulkPickSession";
const stalePickedReleaseKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/stalePickedRelease";
const sameBinSortModeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/sameBinSortMode";
const showOrderNotesInPickSessionKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/showOrderNotesInPickSession";
const preferFullPickTitleKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/preferFullPickTitle";
const allowPaymentPendingPicksKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/allowPaymentPendingPicks";
const zonePickingModeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/pickSession/zonePickingMode";
const rejectUnknownItemsFromPicksKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/rejectUnknownItemsFromPicks";
const hideManualToteAssignmentKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/hideManualToteAssignment";
const preferRedoBinsOverMarketplaceKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/preferRedoBinsOverMarketplace";
const lotTrackingKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/lotTracking";
const barcodeLabelSizeKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/barcodeLabelSize";
const binNamingKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/binNaming";
const defaultFulfillmentLocationExternalIdKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/wms/defaultFulfillmentLocationExternalId";

const autoSplitConditionsKey = "outboundLabels/autoSplit/splitConditions";
const autoSplitGroupId = "fulfillment.auto_split";

const orderManagementSettings = [
  {
    id: "outboundLabels/customs",
    groupId: "fulfillment.international_shipping",
    label: "Default customs information",
    searchLabels: [
      "Default content type",
      "Non-delivery option",
      "Customs declarations",
      "Predefined customs declarations",
      "Default contents description",
      "Sign customs as...",
      "Shipper email",
      "AES exemption code",
      "USPS Exemption Code",
      "ITN usage",
    ],
    description:
      "Set customs declaration defaults, descriptions, signer, and export exemptions for international shipments.",
  },
  {
    id: "outboundLabels/documents",
    groupId: "fulfillment.printing",
    label: "Documents",
    description:
      "Manage the content and automatic behavior for labels, packing slips, pick lists, and Personalized Inserts.",
    searchLabels: [
      "Shipping label content",
      "Packing slip content and automation",
      "Personalized insert content",
      "Pick list content",
      "Local pickup labels",
      "Scan-to-verify printing",
      "One-click print composition",
      "Packing slip template",
      "Add marketplace tag on packing slip print",
      "Tag name",
      "Update marketplace order status when packing slip is printed",
      "Personalized insert template",
      "Print personalized insert when label is printed",
      "Order Number",
      "Secondary Order Name",
      "SKU",
      "Product Name",
      "Customer ID",
      "Order items by",
      "Font size",
      "Image size",
      "Split quantity by order",
      "Show all custom attributes",
      "Enable local pickup labels",
      "Auto-print personalized insert",
      "Auto print packing slip",
      "Workstation",
      "Delay by",
    ],
  },
  {
    id: "outboundLabels/defaultActionButton",
    groupId: "fulfillment.print_actions",
    label: "Default action button",
    description: "Choose the primary fulfillment action shown to operators.",
  },
  {
    id: "outboundLabels/defaultOneClickPrintBatchSize",
    groupId: "fulfillment.print_actions",
    label: "Default batch size for one-click print",
    description:
      "The default number of orders included when starting a one-click print batch.",
  },
  {
    id: "outboundLabels/address/autoCleanAddresses",
    groupId: "fulfillment.address",
    label: "Auto clean addresses",
    description:
      "Automatically standardize minor address details, such as street abbreviations.",
  },
  {
    id: "outboundLabels/address/enableAddressComparison",
    groupId: "fulfillment.address",
    label: "Reuse a customer’s verified address for close matches",
    description:
      "Clean up shipping addresses and hold orders that need correction.",
  },
  {
    id: "outboundLabels/address/autoPlaceHoldOnAddressVerificationFailure",
    groupId: "fulfillment.address",
    label: "Hold orders with unverified shipping addresses",
    description:
      "Clean up shipping addresses and hold orders that need correction.",
  },
  {
    id: "outboundLabels/address/autoPlaceHoldOnAddressVerificationFailureDurationDays",
    groupId: "fulfillment.address",
    label: "Hold duration",
    description:
      "Release the address-verification hold after this many days; use 0 for no automatic expiry.",
  },
  {
    id: "outboundLabels/address/addressOutreachAutomation",
    groupId: "fulfillment.customer_notifications",
    label: "Email customers to correct unverified shipping addresses",
    description:
      "Email customers about address corrections and pickup readiness.",
  },
  {
    id: "outboundLabels/address/addressVerificationReplyToEmail",
    groupId: "fulfillment.customer_notifications",
    label: "Reply-to email",
    description:
      "Replies go to this address, or your connected support email if left blank.",
  },
  {
    id: "outboundLabels/address/addressVerificationCcEmail",
    groupId: "fulfillment.customer_notifications",
    label: "CC email",
    description:
      "This address will be CC'd on all address verification emails sent to customers.",
  },
  {
    id: "outboundLabels/blockPrintingBeyondInventory",
    groupId: "fulfillment.safeguards",
    label: "Prevent label creation for out-of-stock items",
    description: "Applies only to inventory tracked by Redo.",
  },
  {
    id: "outboundLabels/pinChecks/enabled",
    groupId: "fulfillment.safeguards",
    label: "Require PIN approval for selected fulfillment actions",
    searchLabels: [
      "4-digit team PIN for fulfillment approvals",
      "Verify items without scanning",
      "Void labels",
      "Reship orders",
      "Reprint labels",
      "Reprint packing slips",
      "Continue scanning with a packing slip outdated by merging",
      "Merge shipments after a packing slip has been printed",
      "Override an item’s “Not received” status",
      "Cancel orders",
      "Mark as fulfilled",
      "Create purchase order",
      "Skip warehouse bin selection",
      "Bypass parcel photo requirement",
      "Purchase labels at or above a set price",
      "Label price threshold",
      "Purchase labels for packages with no weight entered",
      "Change shipping service",
    ],
    description:
      "Require approval to verify items without scanning, void labels, reship orders, reprint labels or packing slips, cancel orders, mark as fulfilled, create purchase orders, bypass warehouse bins or parcel photos, and change shipping services.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/enabled",
    groupId: "fulfillment.package_photos",
    label: "Enable package photos",
    description:
      "Capture photos of packed packages for dispute evidence and returns reference.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/minCount",
    groupId: "fulfillment.package_photos",
    label: "Minimum required photos",
    description:
      "Required before buying or printing a label. Set to 0 to make photos optional.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/maxCount",
    groupId: "fulfillment.package_photos",
    label: "Maximum photos per package",
    description:
      "Capture photos of packed packages for dispute evidence and returns reference.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/autoCapture/enabled",
    groupId: "fulfillment.package_photos",
    label: "Auto-capture a package photo after verification",
    description:
      "Uses the selected camera in Scan to Verify to capture a photo once the package is still.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/autoCapture/motionThreshold",
    groupId: "fulfillment.package_photos",
    label: "Motion sensitivity threshold",
    description: "Lower values require the package to be more still.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/autoCapture/countdownMs",
    groupId: "fulfillment.package_photos",
    label: "Capture countdown (ms)",
    description:
      "Capture photos of packed packages for dispute evidence and returns reference.",
  },
  {
    id: "outboundLabels/verifyParcelPhotos/autoCapture/armDelayMs",
    groupId: "fulfillment.package_photos",
    label: "Delay after verification (ms)",
    description: "Wait before checking for a still package.",
  },
  {
    id: "outboundLabels/weightFormat",
    groupId: "fulfillment.display",
    label: "Weight unit",
    description: "Display & units",
  },
  {
    id: "outboundLabels/degroupItemsByCustomAttributes",
    groupId: "fulfillment.display",
    label: "Show items separately when custom attributes differ",
    description: "Display & units",
  },
  {
    id: "outboundLabels/hideUnderscoreCustomAttributes",
    groupId: "fulfillment.display",
    label: 'Hide custom attributes starting with "_"',
    description: "Display & units",
  },
  {
    id: "outboundLabels/liveViewCountsEnabled",
    groupId: "fulfillment.display",
    label: "Show live unfulfilled order counts in the fulfillment sidebar",
    description: "Display & units",
  },
  {
    id: "outboundLabels/billOfMaterials/alwaysHideInputItems",
    groupId: "fulfillment.bundles",
    label:
      "Show bundles instead of components on packing slips, pick lists, and Scan to Verify",
    description: "Inventory still deducts the component items.",
  },
  {
    id: "outboundLabels/billOfMaterials/alwaysExpandBomIgnoreInventory",
    groupId: "fulfillment.bundles",
    label:
      "Include components for every bundle, even when assembled stock is available",
    description:
      "Pick lists, packing slips, and Scan to Verify include components for all ordered bundles, not just those without assembled stock. Your bundle display settings still apply.",
  },
  {
    id: "outboundLabels/billOfMaterials/showBomInputsInGroupTable",
    groupId: "fulfillment.bundles",
    label: "Show bundle components in the fulfillment table",
    description: "Bundles",
  },
  {
    id: "outboundLabels/preferredReturnLabelCarrier",
    groupId: "fulfillment.return_label_defaults",
    label: "Preferred carrier",
    description:
      "With no preference, Redo tries the original shipment's carrier, then the cheapest eligible rate.",
  },
  {
    id: "outboundLabels/preferredReturnLabelService",
    groupId: "fulfillment.return_label_defaults",
    label: "Preferred service",
    description: "For return labels created during outbound fulfillment.",
  },
  {
    id: "outboundLabels/sendPickupEmailOnOverride",
    groupId: "fulfillment.customer_notifications",
    label: "Email customers when converted pickup orders are ready",
    description:
      "Notify customers when a shipping order converted to pickup is ready.",
  },
  {
    id: "outboundLabels/customMetadata",
    groupId: "fulfillment.custom_field_mappings",
    label: "Custom field mappings",
    searchLabels: [
      "Order metafields",
      "Custom attributes",
      "Product metafields",
      "Variant metafields",
      "Where is the field stored?",
      "What is this field associated with?",
      "What kind of order field?",
      "Namespace",
      "Value type",
      "Name in Redo (optional)",
      "Use for scanning",
    ],
    description:
      "Choose order metafields, custom attributes, product metafields, and variant metafields for fulfillment columns and CSV exports.",
  },
  {
    id: "outboundLabels/variantReceivedNamespace",
    groupId: "fulfillment.custom_field_mappings",
    label: "Received metafield namespace",
    description:
      "The namespace of the Shopify variant metafield that tracks whether an item has been received.",
  },
  {
    id: "outboundLabels/variantReceivedKey",
    groupId: "fulfillment.custom_field_mappings",
    label: "Received metafield key",
    searchLabels: ["Open - Not Received", "Not received status"],
    description:
      "Orders with a variant where this metafield is false stay Open - Not Received. Leave blank to treat every item as received.",
  },
  {
    id: "outboundLabels/suggestMerge/enabled",
    groupId: "fulfillment.merge_suggestions",
    label: "Suggest merging fulfillments that meet all the selected criteria",
    description:
      "Turn on merge suggestions for fulfillments that meet all the selected criteria.",
  },
  {
    id: "outboundLabels/autoMerge/enabled",
    groupId: "fulfillment.auto_merge",
    label:
      "Automatically merge fulfillments that meet all the selected criteria",
    description:
      "Automatically merge fulfillments that meet all the selected criteria",
  },
  {
    id: "outboundLabels/autoMerge/refundOtherShippingOptions",
    groupId: "fulfillment.merge_outcomes",
    label: "Refund other shipping charges",
    description:
      "Keep the selected shipping service's charge and refund the rest, where supported.",
  },
  {
    id: "outboundLabels/autoMerge/addTags/enabled",
    groupId: "fulfillment.merge_outcomes",
    label: "Add marketplace order tags to auto-merged orders",
    description:
      "Add the selected marketplace order tags after an automatic merge.",
  },
  {
    id: "outboundLabels/autoMerge/addNote/enabled",
    groupId: "fulfillment.merge_outcomes",
    label: "Add a marketplace note to auto-merged orders (Shopify only)",
    description: "Add a marketplace note to auto-merged orders (Shopify only)",
  },
  {
    id: "outboundLabels/autoSplit/enabled",
    groupId: "fulfillment.auto_split",
    label: "Split items matching the selected criteria into separate shipments",
    description:
      "Split items matching the selected criteria into separate shipments",
  },
  {
    id: "outboundLabels/autoSplit/splitIntoIndividualShipments",
    groupId: "fulfillment.split_outcomes",
    label: "Split into individual shipments",
    description:
      "Create a separate shipment for each matching unit, including multiple units of the same item.",
  },
  {
    id: "outboundLabels/autoSplit/leaveOne",
    groupId: "fulfillment.split_outcomes",
    label: "Keep one unit in the original shipment",
    description:
      "Keep one matching unit in the original shipment when its _leave_one attribute is true.",
  },
  {
    id: "outboundLabels/autoSplit/addTags/enabled",
    groupId: "fulfillment.split_outcomes",
    label: "Add Marketplace order tag after splitting",
    description: "Add Marketplace order tag after splitting",
  },
  {
    id: "outboundLabels/autoMerge/serviceLevelPriority",
    groupId: "fulfillment.merge_outcomes",
    label: "Shipping service to keep after merging",
    description:
      "Choose which original order's shipping service to keep after merging.",
  },
  {
    id: "outboundLabels/autoMerge/exceptionConditions",
    groupId: "fulfillment.merge_exceptions",
    label: "Auto-merge exceptions",
    searchLabels: [
      "Marketplace order tag is any of",
      "Pick list printed",
      "Order is older than",
      "Sales channel is any of",
      "Product name includes any of",
      "Marketplace product tag is any of",
      "Redo shipment tag is any of",
      "SKU is any of",
      "Shipment has missing bins",
      "Shipment has missing warehouse bins",
      "Number of orders is at least",
      "Merged item quantity would exceed",
    ],
    description:
      "Prevent merging shipments that match any of the selected criteria: Marketplace order tag is any of, Pick list printed, Order is older than, Sales channel is any of, Product name includes any of, Marketplace product tag is any of, Redo shipment tag is any of, SKU is any of, Shipment has missing bins, Shipment has missing warehouse bins, Number of orders is at least, Merged item quantity would exceed.",
  },
  {
    id: "outboundLabels/autoSplit/productMappings",
    groupId: "fulfillment.split_sku_mappings",
    label: "Product SKU mappings",
    searchLabels: ["Trigger SKU", "Companion SKUs"],
    description:
      "Keep Companion SKUs with a Trigger SKU when it moves to a separate shipment.",
  },
  {
    id: "outboundLabels/autoSplit/exceptionConditions/orderTags",
    groupId: "fulfillment.split_exceptions",
    label: "Split exceptions",
    searchLabels: ["Marketplace order tag is any of"],
    description:
      "Prevent splitting orders when Marketplace order tag is any of the selected tags.",
  },
] as const;

export const FulfillmentFlatTeamSettingCatalogDefinitions = [
  ...orderManagementSettings.map(({ id, groupId, ...setting }) => ({
    id,
    groupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting,
      group: FulfillmentFlatTeamSettingGroupById[groupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings" as const, key: id },
  })),
  {
    id: "outboundLabels/suggestMerge/mergeConditions",
    groupId: "fulfillment.merge_suggestions",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Merge suggestions",
        searchLabels: [
          "Ship-from addresses match",
          "Delivery addresses match",
          "Delivery methods match",
          "Orders belong to the same customer record",
          "Customer names match",
          "Packing slips have not been printed",
          "Payment information matches",
          "Billing addresses match",
          "Recipient names match",
          "Order IDs do not match",
          "Fulfillment status",
        ],
        description:
          "Choose matching requirements for merge suggestions, including addresses, customer details, payment information, packing-slip status, delivery method, different order IDs, and shipment status. Ship-from matching is always required. Criteria: Ship-from addresses match, Delivery addresses match, Delivery methods match, Orders belong to the same customer record, Customer names match, Packing slips have not been printed, Payment information matches, Billing addresses match, Recipient names match, Order IDs do not match, Fulfillment status.",
      },
      group:
        FulfillmentFlatTeamSettingGroupById["fulfillment.merge_suggestions"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/suggestMerge/mergeConditions",
    },
  },
  {
    id: "outboundLabels/autoMerge/mergeConditions",
    groupId: "fulfillment.auto_merge",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Auto-merge",
        searchLabels: [
          "Ship-from addresses match",
          "Delivery addresses match",
          "Delivery methods match",
          "Orders belong to the same customer record",
          "Customer names match",
          "Packing slips have not been printed",
          "Payment information matches",
          "Billing addresses match",
          "Recipient names match",
          "Order IDs do not match",
          "Fulfillment status",
        ],
        description:
          "Choose matching requirements for automatic shipment merging. Delivery address, ship-from address, and delivery method must match. Auto-merge allows only open shipments, or open and payment pending shipments. Exceptions and post-merge actions are configured separately. Criteria: Ship-from addresses match, Delivery addresses match, Delivery methods match, Orders belong to the same customer record, Customer names match, Packing slips have not been printed, Payment information matches, Billing addresses match, Recipient names match, Order IDs do not match, Fulfillment status.",
      },
      group: FulfillmentFlatTeamSettingGroupById["fulfillment.auto_merge"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/autoMerge/mergeConditions",
    },
  },
  {
    id: "outboundLabels/learnedPresets/parcel",
    groupId: "fulfillment.remember_shipment_choices",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Remember package choice",
        description:
          "Reuse the package selected for shipments with matching contents.",
        synonyms: ["remember package choice"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById[
          "fulfillment.remember_shipment_choices"
        ],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/learnedPresets/parcel",
    },
  },
  {
    id: "outboundLabels/learnedPresets/weight",
    groupId: "fulfillment.remember_shipment_choices",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Remember package weight",
        description:
          "Reuse the weight entered for shipments with matching contents.",
        synonyms: ["remember package weight"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById[
          "fulfillment.remember_shipment_choices"
        ],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/learnedPresets/weight",
    },
  },
  {
    id: "outboundLabels/learnedPresets/keyLevel",
    groupId: "fulfillment.remember_shipment_choices",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Remember choices per",
        description:
          "Remember package and weight choices per variant, or per parent product so every variant of a product shares them.",
        synonyms: [
          "remember per parent product",
          "parent product memory",
          "remember per variant",
        ],
      },
      group:
        FulfillmentFlatTeamSettingGroupById[
          "fulfillment.remember_shipment_choices"
        ],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/learnedPresets/keyLevel",
    },
  },
  {
    id: "outboundLabels/batchSuggestions/statusOption",
    groupId: "fulfillment.batch_suggestions",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Fulfillment status",
        description:
          "Choose which shipment statuses qualify for batch suggestions.",
        synonyms: ["batch shipment statuses"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById["fulfillment.batch_suggestions"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/batchSuggestions/statusOption",
    },
  },
  {
    id: "outboundLabels/batchSuggestions/minGroupCount",
    groupId: "fulfillment.batch_suggestions",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Minimum number of matching shipments",
        description:
          "Require this many matching shipments before suggesting a batch.",
        synonyms: ["minimum batch shipments"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById["fulfillment.batch_suggestions"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/batchSuggestions/minGroupCount",
    },
  },
  {
    id: "outboundLabels/batchSuggestions/groupingKey",
    groupId: "fulfillment.batch_suggestions",
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Group suggested items in bulk shipments by",
        description:
          "Match items by variant ID or SKU when suggesting shipment batches.",
        synonyms: ["batch item matching"],
      },
      group:
        FulfillmentFlatTeamSettingGroupById["fulfillment.batch_suggestions"],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: "outboundLabels/batchSuggestions/groupingKey",
    },
  },
  {
    id: shipFromLocationKey,
    groupId: shipFromLocationGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Ship from locations",
        description:
          "Selects the ship-from location used automatically when creating a manual order.",
        synonyms: [
          "default ship from location",
          "default warehouse",
          "manual order location",
          "shipping origin default",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[shipFromLocationGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: shipFromLocationKey },
  },
  {
    id: emphasizeNotesScreenKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Emphasize notes section when notes are present",
        description:
          "Highlights the Notes section with a yellow background on Scan to verify whenever notes are present. Turn this off to use the standard Notes background.",
        synonyms: ["highlight scan notes", "emphasize packing notes"],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: emphasizeNotesScreenKey },
  },
  {
    id: disableKeyboardOnScanKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Disable manual entry in the scan barcode field",
        description:
          "Prevents non-admin operators from typing or pasting into the scan barcode field while continuing to accept hardware barcode scans. Separately enabled manual quantity entry remains available.",
        synonyms: [
          "disable manual barcode entry",
          "barcode scanner only",
          "prevent typing barcode",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: disableKeyboardOnScanKey },
  },
  {
    id: moveScannedItemToTopKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Move scanned item to top of the list",
        description:
          "Keeps partially scanned items at the top of the verification list, ordered with the most recently scanned item first.",
        synonyms: [
          "move scanned item to top",
          "pin scanned items",
          "sort active scans first",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: moveScannedItemToTopKey },
  },
  {
    id: moveVerifiedItemsToBottomKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Move verified items to bottom of the list",
        description:
          "Moves fully verified items below the remaining items after a brief completion animation.",
        synonyms: [
          "move verified items to bottom",
          "sort completed scans last",
          "hide verified items below",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: moveVerifiedItemsToBottomKey },
  },
  {
    id: autoVerifyLabeledOrdersKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Verify all items when opening a labeled order",
        description:
          "Verifies every item as soon as an order that already has a shipping label opens on scan-to-verify, so scanning each label confirms the package left the warehouse. Orders needing serial numbers, strict license plate scans, or problem order resolution still scan item by item.",
        synonyms: [
          "verify all items when opening a labeled order",
          "auto verify items",
          "auto verify labeled orders",
          "skip item scanning",
          "verify all items automatically",
          "bulk printed labels",
          "scan label to confirm shipped",
          "confirm package left warehouse",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: autoVerifyLabeledOrdersKey },
  },
  {
    id: manualQuantityEntryKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label:
          "Allow users to manually enter item quantity when line item quantity is greater than",
        description:
          "Lets operators type the verified quantity for larger line items instead of scanning every unit. The threshold determines which line items show an editable quantity input.",
        synonyms: [
          "manual scan quantity",
          "scan to verify quantity",
          "enter verified item quantity",
          "bulk item verification",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: manualQuantityEntryKey },
  },
  {
    id: allowManualQuantityAfterBarcodeScanKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Allow manual quantity after scanning barcode",
        description:
          "Requires operators to scan a line item's barcode on Scan to verify before they can edit its verified quantity. Once the barcode is scanned, the Scanned column becomes an editable quantity input for line items with a quantity above 1, so operators can enter the remaining units instead of scanning each one.",
        synonyms: [
          "allow manual quantity after scanning barcode",
          "edit quantity after scan",
          "manual quantity after barcode scan",
          "scan barcode then enter quantity",
          "require barcode scan before quantity entry",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: allowManualQuantityAfterBarcodeScanKey,
    },
  },
  {
    id: labelAutoPrintTriggerKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Label auto-print trigger",
        description:
          "Sets the team default for when Scan to verify prints shipping labels: after the first item, after all items, or manually. Workstations can override this default.",
        synonyms: [
          "label auto-print trigger",
          "print label when first item is scanned",
          "print label when all items are scanned",
          "auto print shipping label",
          "automatic label printing",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: labelAutoPrintTriggerKey },
  },
  {
    id: internationalAddressOutreachKey,
    groupId: addressGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Apply address outreach and holds outside the US",
        description:
          "Applies enabled address holds and correction emails to non-US addresses. Set up emails under Customer notifications.",
        synonyms: [
          "international address verification",
          "international address emails",
          "non-US addresses",
          "overseas orders address",
          "address outreach international",
        ],
        changeGuidance: {
          summary:
            "Turn on email outreach or address holds before editing this setting. When both are off, the toggle is disabled and its saved selection is preserved.",
        },
      },
      group: FulfillmentFlatTeamSettingGroupById[addressGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: internationalAddressOutreachKey,
    },
  },
  {
    id: perParcelSlipsKey,
    groupId: printingGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Print a packing slip per parcel",
        description:
          "When an order ships in more than one box, prints a separate packing slip for each box listing only the items allocated to it, and names that box in the slip's footer. Off by default: with it off, a multi-box order gets one slip listing every item in the order.",
        synonyms: [
          "packing slip per parcel",
          "packing slip per box",
          "one slip per box",
          "slip for each package",
          "multi parcel packing slips",
          "split packing slip by box",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[printingGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: perParcelSlipsKey },
  },
  {
    id: emphasizeQuantityKey,
    groupId: printingGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Emphasize quantity",
        description:
          "Draws an outline circle around quantities greater than 1 on pick lists so pickers can spot multi-unit lines. On by default.",
        synonyms: [
          "emphasize quantity",
          "pick list emphasize quantity",
          "pick list quantity circle",
          "outline quantity",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[printingGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: emphasizeQuantityKey },
  },
  {
    id: pickListPerOrderKey,
    groupId: printingGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "One pick list per order",
        description:
          "When printing pick lists for a batch, renders a separate pick list for each order instead of one combined list. All pick lists are merged into one PDF, with each order starting on a new page and its order number in the header. Off by default.",
        synonyms: [
          "pick list per order",
          "one pick list per order",
          "separate pick lists",
          "individual pick lists",
          "pick list for each order",
          "split pick list by order",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[printingGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: pickListPerOrderKey },
  },
  {
    id: shippingLabelCustomSectionKey,
    groupId: printingGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Custom label section",
        description:
          "Shrinks the carrier's shipping label to print your enabled label fields in a section at the bottom, styled and previewed in the label layout editor. Off by default; when off, labels print exactly as the carrier returns them.",
        synonyms: [
          "custom label section",
          "shipping label layout",
          "label layout editor",
          "custom content on shipping label",
          "add design to bottom of label",
          "shrink shipping label",
        ],
        risk: {
          level: "medium",
          reason:
            "Changes every printed shipping label for affected carriers, and some carriers may reject labels with customizations.",
        },
        availability: {
          conditions: [
            {
              type: "evaluator",
              evaluator: "featureFlag.enabled",
              featureFlag: shippingLabelBuilderFlag.key,
            },
          ],
          unavailableBehavior: "hidden",
          reason:
            "Only available while the label layout editor is rolled out to the team.",
        },
      },
      group: FulfillmentFlatTeamSettingGroupById[printingGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: shippingLabelCustomSectionKey,
    },
  },
  {
    id: carrierConnectionInterruptedKey,
    groupId: outboundNotificationsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "When a carrier account needs to be reconnected",
        description:
          "Emails the outbound labels notification addresses when a connected carrier account (such as UPS) loses its authorization and needs to be reconnected.",
        synonyms: [
          "carrier connection interrupted",
          "carrier disconnected email",
          "ups reauthorization alert",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[outboundNotificationsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: carrierConnectionInterruptedKey,
    },
  },
  {
    id: transferPutawayOrderKey,
    groupId: transfersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Transfer put-away order",
        description:
          "Chooses whether the mobile app walks transfer put-away in the order items were scanned or along the warehouse map route through the suggested bins.",
        synonyms: [
          "transfer put-away order",
          "put-away walk order",
          "transfer route order",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[transfersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: transferPutawayOrderKey },
  },
  {
    id: excludeEmptyTransferDestinationsKey,
    groupId: transfersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Don't suggest empty destination bins",
        description:
          "When on, transfer put-away only suggests destination bins that already hold the product. Bins where the product is at 0 are skipped; if every bin is empty, the product has no suggested bin and staff choose one.",
        synonyms: [
          "skip empty bins",
          "empty destination bins",
          "zero quantity bins",
          "transfer destination suggestion",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[transfersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: excludeEmptyTransferDestinationsKey,
    },
  },
  {
    id: emptyInventoryLevelCleanupKey,
    groupId: transfersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Clear empty bin rows",
        description:
          "An hourly job removes a product's 0-quantity row from a bin once it has stayed at 0 for the set number of hours or days. Configured separately for pickable, storage, and receiving bins; all off by default.",
        synonyms: [
          "clear empty bins",
          "remove zero quantity rows",
          "empty inventory level cleanup",
          "auto clear empty bin assignments",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[transfersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: emptyInventoryLevelCleanupKey,
    },
  },
  {
    id: receivingModeKey,
    groupId: transfersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Receiving mode",
        description:
          "Chooses which receiving flows the warehouse dashboard offers: receive into the receiving area, receive and place straight into bins, or both.",
        synonyms: [
          "receiving mode",
          "receive and place only",
          "hide receive and place",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[transfersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: receivingModeKey },
  },
  {
    id: forceSingleItemPickSessionsKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Pick session strategy",
        description:
          "Forces every pick session to be single-item, so single and multi-item orders are picked together into shared totes and the multi-item option is hidden from pickers.",
        synonyms: [
          "force single-item pick sessions",
          "single item pick sessions only",
          "hide multi-item pick sessions",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: forceSingleItemPickSessionsKey,
    },
  },
  {
    id: scanOncePerProductKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Scan once per product",
        description:
          "Scanning a product with several units counts all of them instead of requiring one scan per unit. When picking from bins it opens a confirmation prefilled with the full quantity; in scan-at-cart sessions and when sorting at the wall it places all of an order's remaining units of that product in its tote.",
        synonyms: [
          "scan once per product",
          "confirm total pick quantity",
          "skip scanning every unit",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: scanOncePerProductKey },
  },
  {
    id: defaultReplenishmentQuantityKey,
    groupId: replenishmentGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Default replenishment quantity",
        description:
          "Number of units moved into a pickable bin each time a product is replenished. Never more than the source bin has available. When blank, replenishment tops bins up to cover open demand.",
        synonyms: [
          "default replenishment quantity",
          "units per replenishment",
          "replenishment amount",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[replenishmentGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: defaultReplenishmentQuantityKey,
    },
  },
  {
    id: dashboardActionsKey,
    groupId: dashboardGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Dashboard actions",
        description:
          "Chooses which actions the warehouse dashboard shows to everyone, with per-role overrides for warehouse users, packers, pickers, and replenishers.",
        synonyms: [
          "dashboard actions",
          "hide warehouse dashboard actions",
          "dashboard actions by role",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[dashboardGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: dashboardActionsKey },
  },
  {
    id: scanTypeKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Tote scanning",
        description:
          "Chooses whether pickers scan totes at the wall or at each bin while picking.",
        synonyms: ["tote scanning", "scan at wall"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: scanTypeKey },
  },
  {
    id: defaultOrderCountKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Default order count",
        description:
          "Sets how many orders a new single-item and multi-item pick session suggests by default.",
        synonyms: [
          "default order count",
          "orders per pick session",
          "single-item default order count",
          "multi-item default order count",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: defaultOrderCountKey },
  },
  {
    id: maxItemCountKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Item limit for multi-item sessions",
        description: "Caps how many items a multi-item pick session can hold.",
        synonyms: [
          "item limit for multi-item sessions",
          "max items per pick session",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: maxItemCountKey },
  },
  {
    id: bulkPickSessionKey,
    groupId: workOrdersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Bulk pick session limits",
        description:
          "Caps the work orders and pick lines a bulk pick session can include, and the default work order count it suggests.",
        synonyms: [
          "bulk pick session limits",
          "bulk pick max work orders",
          "maximum work orders per bulk pick session",
          "maximum pick lines per bulk pick session",
          "suggested work orders per bulk pick session",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[workOrdersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: bulkPickSessionKey },
  },
  {
    id: stalePickedReleaseKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Stale pick session release",
        description:
          "Releases orders back to the queue after they sit in the picked status for too many days.",
        synonyms: ["stale pick session release", "release stuck picked orders"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: stalePickedReleaseKey },
  },
  {
    id: sameBinSortModeKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Same-bin sort order",
        description:
          "Breaks ties between items routed to the same bin when a pick wave is created.",
        synonyms: ["same-bin sort order", "pick wave tiebreaker"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: sameBinSortModeKey },
  },
  {
    id: showOrderNotesInPickSessionKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Order notes in pick sessions",
        description:
          "Shows the customer's order note on each item while picking.",
        synonyms: [
          "order notes in pick sessions",
          "show order note to pickers",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: showOrderNotesInPickSessionKey,
    },
  },
  {
    id: preferFullPickTitleKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Show full product titles while picking",
        description:
          "Shows each item's product and variant title together while picking, instead of only the variant or its size and color.",
        synonyms: [
          "show full product titles while picking",
          "full pick title",
          "product and variant title when picking",
          "product title in pick sessions",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: preferFullPickTitleKey },
  },
  {
    id: allowPaymentPendingPicksKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Allow picking payment-pending orders",
        description:
          "Lets pickers start orders whose payment is still pending.",
        synonyms: [
          "allow picking payment-pending orders",
          "pick unpaid orders",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: allowPaymentPendingPicksKey },
  },
  {
    id: zonePickingModeKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Zone picking",
        description:
          "Chooses whether pick sessions are split by warehouse zone and in what order zones are worked.",
        synonyms: ["zone picking", "pick by zone"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: zonePickingModeKey },
  },
  {
    id: rejectUnknownItemsFromPicksKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Items not on the pick list",
        description:
          "Rejects scanned items that are not part of the current pick instead of adding them.",
        synonyms: [
          "items not on the pick list",
          "reject unknown items when picking",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: rejectUnknownItemsFromPicksKey,
    },
  },
  {
    id: hideManualToteAssignmentKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Manual tote assignment",
        description:
          "Hides the manual tote assignment step so totes are always assigned by scanning.",
        synonyms: ["manual tote assignment", "hide tote assignment"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: hideManualToteAssignmentKey },
  },
  {
    id: preferRedoBinsOverMarketplaceKey,
    groupId: pickSessionsGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Which bins to pull from first",
        description:
          "Chooses whether picking uses bins assigned in Redo before bins from Shopify or other marketplace metafields. When on, bin changes in Redo also update the Shopify bin metafield.",
        synonyms: ["which bins to pull from first", "bin source preference"],
      },
      group: FulfillmentFlatTeamSettingGroupById[pickSessionsGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: preferRedoBinsOverMarketplaceKey,
    },
  },
  {
    id: lotTrackingKey,
    groupId: transfersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Lot tracking",
        description:
          "Excludes expired lots from the sales channel and blocks picking for a period after receiving lot-tracked inventory.",
        synonyms: [
          "lot tracking settings",
          "exclude expired lots",
          "days before expiration to stop picking lots",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[transfersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: lotTrackingKey },
  },
  {
    id: barcodeLabelSizeKey,
    groupId: workOrdersGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Work order label size",
        description:
          "Chooses the default label size when printing barcode labels from work orders.",
        synonyms: ["work order barcode label size", "bin label size"],
      },
      group: FulfillmentFlatTeamSettingGroupById[workOrdersGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: barcodeLabelSizeKey },
  },
  {
    id: binNamingKey,
    groupId: generalGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Bin naming",
        description:
          "Defines how bin names are built from row, rack, shelf and bin, including the advanced naming template.",
        synonyms: ["bin naming", "bin naming template"],
      },
      group: FulfillmentFlatTeamSettingGroupById[generalGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: binNamingKey },
  },
  {
    id: defaultFulfillmentLocationExternalIdKey,
    groupId: generalGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Default fulfillment location",
        description: "Chooses the location warehouse devices open by default.",
        synonyms: [
          "default fulfillment location",
          "default warehouse location",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[generalGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: defaultFulfillmentLocationExternalIdKey,
    },
  },
  {
    id: autoSplitConditionsKey,
    groupId: autoSplitGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Auto-split conditions",
        searchLabels: [
          "SKU",
          "Product name",
          "Marketplace product tags",
          "Receiving status",
          "Line item custom attribute",
        ],
        description:
          "Split matching items into separate shipments when auto-split is enabled. Saved conditions are preserved when auto-split is off.",
        synonyms: [
          "auto-split conditions",
          "auto split criteria",
          "split order conditions",
          "product split rules",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[autoSplitGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: autoSplitConditionsKey },
  },
] satisfies readonly SettingDefinition<
  FulfillmentFlatTeamSettingKey,
  FulfillmentFlatTeamSettingGroupId,
  TeamSettingsSettingValueSource<FulfillmentFlatTeamSettingKey>
>[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const FulfillmentFlatTeamSettingMetadataByKey = Object.fromEntries(
  FulfillmentFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<FulfillmentFlatTeamSettingKey, SettingMetadata>;
